import { z } from 'zod';
import { EvidenceTypeSchema } from '../catalogs/domain-catalogs.js';
import { TimestampSchema } from '../common/clock.js';
import { AgentSessionIdSchema, ApplicationIdSchema, CreditLineIdSchema, CustomerIdSchema, DocumentIdSchema, OfferIdSchema, } from '../common/ids.js';
import { MoneySchema, PositiveMoneySchema } from '../common/money.js';
import { paginatedSchema, PaginationQuerySchema } from '../common/pagination.js';
import { RateSchema } from '../common/rate.js';
import { SimulationResultSchema } from '../credit/schemas.js';
import { ConsentTypeSchema } from '../identity/customer.js';
import { ReasonCodeSchema } from '../reason-codes.js';
import { KnockOutRuleSchema, ReviewAlertSchema } from './policies.js';
import { ApplicationPhaseSchema, ApplicationStatusSchema, CreditDecisionOutcomeSchema, DecisionMakerSchema, DocumentVerdictSchema, EvidenceDocumentStatusSchema, FormalizationStatusSchema, OfferStatusSchema, PhaseActorSchema, RecommendationSchema, } from './states.js';
export const ApplicationChannelSchema = z.enum(['WEB', 'WIDGET', 'VOICE', 'CONSOLE']);
/** `POST /v1/applications` (Idem ✔). */
export const CreateApplicationRequestSchema = z
    .object({
    requestedAmount: PositiveMoneySchema.optional(),
    purpose: z.string().max(280).optional(),
    channel: ApplicationChannelSchema,
})
    .meta({ id: 'CreateApplicationRequest' });
export const ChecklistItemSchema = z.object({
    code: z.string(),
    label: z.string(),
    phase: ApplicationPhaseSchema,
    status: z.enum(['PENDING', 'IN_REVIEW', 'DONE', 'NOT_APPLICABLE']),
    required: z.boolean(),
});
export const BusinessProfileSchema = z
    .object({
    businessName: z.string().optional(),
    businessType: z.string().optional(),
    activityDescription: z.string().optional(),
    businessAgeMonths: z.int().min(0).optional(),
    municipalityCode: z
        .string()
        .regex(/^\d{5}$/)
        .optional(),
    address: z.string().optional(),
    premises: z.enum(['OWNED', 'RENTED', 'HOME_BASED', 'MOBILE']).optional(),
    employees: z.int().min(0).optional(),
    salesChannels: z.array(z.string()).optional(),
    /** Declarado por el cliente (fuente `DECLARED` del flujo de caja). Montos mensuales en COP. */
    declaredMonthlySales: MoneySchema.optional(),
    declaredMonthlyPurchases: MoneySchema.optional(),
    declaredMonthlyExpenses: MoneySchema.optional(),
})
    .meta({ id: 'BusinessProfile' });
export const HouseholdProfileSchema = z
    .object({
    dependents: z.int().min(0).optional(),
    householdSize: z.int().min(1).optional(),
    housing: z.enum(['OWNED', 'RENTED', 'FAMILY', 'OTHER']).optional(),
    otherIncomeSources: z.array(z.string()).optional(),
    /** Declarado por el cliente. Montos mensuales en COP. */
    declaredMonthlyExpenses: MoneySchema.optional(),
    declaredOtherIncome: MoneySchema.optional(),
    declaredDebtInstallments: MoneySchema.optional(),
})
    .meta({ id: 'HouseholdProfile' });
// ── Documentos ────────────────────────────────────────────────────────────
export const DocumentReviewSchema = z
    .object({
    version: z.int(),
    reviewerType: z.enum(['AGENT', 'ANALYST']),
    detectedType: EvidenceTypeSchema,
    legibility: z.enum(['GOOD', 'FAIR', 'POOR']),
    validity: z.enum(['VALID', 'EXPIRED', 'UNKNOWN']),
    relevance: z.enum(['HIGH', 'MEDIUM', 'LOW', 'NONE']),
    /** Campos tipados por tipo de documento (montos como string decimal). */
    extracted: z.record(z.string(), z.unknown()),
    issues: z.array(z.object({
        code: z.string(),
        message: z.string(),
        severity: z.enum(['INFO', 'WARNING', 'ERROR']),
    })),
    fraudSignals: z.array(z.string()),
    suggestions: z.array(z.string()),
    verdict: DocumentVerdictSchema,
    reviewedAt: TimestampSchema,
})
    .meta({ id: 'DocumentReview' });
export const EvidenceDocumentSchema = z
    .object({
    id: DocumentIdSchema,
    applicationId: ApplicationIdSchema,
    declaredType: EvidenceTypeSchema.nullable(),
    detectedType: EvidenceTypeSchema.nullable(),
    status: EvidenceDocumentStatusSchema,
    mimeType: z.string(),
    sizeBytes: z.int(),
    pageCount: z.int().nullable(),
    uploadedVia: z.enum(['CHAT', 'APP', 'CONSOLE']),
    review: DocumentReviewSchema.nullable(),
    downloadUrl: z.string().nullable(),
    createdAt: TimestampSchema,
})
    .meta({ id: 'EvidenceDocument' });
/** Campos de texto del multipart de `POST /v1/applications/{id}/documents` (el archivo va en `file`). */
export const UploadDocumentFieldsSchema = z.object({ declaredType: EvidenceTypeSchema.optional() });
// ── Flujo de caja ─────────────────────────────────────────────────────────
export const CashFlowSectionSchema = z.enum([
    'SALES',
    'COGS',
    'OPEX',
    'OTHER_INCOME',
    'HOUSEHOLD',
    'DEBT',
    'ASSET',
    'LIABILITY',
]);
export const CashFlowMethodSchema = z.enum(['DECLARED', 'DOCUMENT', 'TRIANGULATED', 'ESTIMATED']);
export const CashFlowLineItemSchema = z
    .object({
    id: z.string(),
    section: CashFlowSectionSchema,
    label: z.string(),
    monthlyAmount: MoneySchema,
    method: CashFlowMethodSchema,
    sources: z.array(z.object({
        documentId: DocumentIdSchema.nullable(),
        memoryFactId: z.string().nullable(),
        excerpt: z.string(),
        weight: z.string(),
    })),
})
    .meta({ id: 'CashFlowLineItem' });
/** Cifras derivadas calculadas por credit-math (nunca por el LLM). */
export const CashFlowAnalysisSchema = z
    .object({
    version: z.int(),
    status: z.enum(['DRAFT', 'FINAL']),
    periodMonths: z.int(),
    monthlySales: MoneySchema,
    costOfSales: MoneySchema,
    grossMargin: MoneySchema,
    operatingExpenses: MoneySchema,
    businessNetIncome: MoneySchema,
    otherIncome: MoneySchema,
    householdExpenses: MoneySchema,
    existingDebtPayments: MoneySchema,
    paymentCapacity: MoneySchema,
    prudentialFactor: z.string(),
    maxInstallment: MoneySchema,
    suggestedLimit: MoneySchema,
    assumptions: z.array(z.string()),
    confidence: z.enum(['LOW', 'MEDIUM', 'HIGH']),
    items: z.array(CashFlowLineItemSchema),
    /** Triangulación de ventas (≥ 2 métodos); divergencia > política → valor conservador + alerta. */
    triangulation: z
        .object({
        estimates: z.array(z.object({ label: z.string(), monthlyAmount: MoneySchema })),
        divergence: z.string(),
        withinTolerance: z.boolean(),
        method: z.enum(['SINGLE_SOURCE', 'AVERAGE', 'CONSERVATIVE']),
    })
        .optional(),
    /** Plazo y tasa de referencia del cupo sugerido (valor presente de la cuota máxima). */
    reference: z.object({ installments: z.int(), rateEA: z.string() }).optional(),
})
    .meta({ id: 'CashFlowAnalysis' });
export const AdjustCashFlowItemRequestSchema = z.object({
    monthlyAmount: MoneySchema,
    reason: z.string().trim().min(5).max(500),
});
// ── Decisión y oferta ─────────────────────────────────────────────────────
export const DecisionRecommendationSchema = z
    .object({
    engineVersion: z.string(),
    recommendation: RecommendationSchema,
    score: z.int().min(0).max(1000),
    recommendedLimit: MoneySchema.nullable(),
    rulesFired: z.array(z.object({ rule: z.string(), outcome: z.string(), detail: z.string().optional() })),
    knockOuts: z.array(KnockOutRuleSchema),
    alerts: z.array(ReviewAlertSchema),
    reasons: z.array(z.string()),
    /** `true` si la política permite decidir sin analista (autoaprobación o autorrechazo). */
    autoDecidable: z.boolean(),
    createdAt: TimestampSchema,
})
    .meta({ id: 'DecisionRecommendation' });
export const CreditDecisionSchema = z
    .object({
    outcome: CreditDecisionOutcomeSchema,
    approvedLimit: MoneySchema.nullable(),
    rateEA: RateSchema.nullable(),
    maxInstallments: z.int().nullable(),
    conditions: z.array(z.string()),
    decidedBy: DecisionMakerSchema,
    decidedByUserId: z.string().nullable(),
    rationale: z.string(),
    reasonCodes: z.array(z.string()),
    decidedAt: TimestampSchema,
})
    .meta({ id: 'CreditDecision' });
/** `POST /v1/admin/applications/{id}/decision` (Idem ✔). Solo analista (A). */
export const AdminDecisionRequestSchema = z
    .object({
    outcome: CreditDecisionOutcomeSchema,
    limit: PositiveMoneySchema.optional(),
    productId: z.string().optional(),
    maxInstallments: z.int().min(1).optional(),
    reasonCodes: z.array(z.string()).default([]),
    comment: z.string().trim().min(5).max(4000),
})
    .meta({ id: 'AdminDecisionRequest' });
export const OfferSchema = z
    .object({
    id: OfferIdSchema,
    applicationId: ApplicationIdSchema,
    approvedLimit: MoneySchema,
    productName: z.string(),
    rateEA: RateSchema,
    rateMV: RateSchema,
    allowedInstallments: z.array(z.int()),
    maxInstallments: z.int(),
    fees: z.object({ mipymeFeeRate: RateSchema, lifeInsuranceMonthlyRate: RateSchema }),
    exampleSimulation: SimulationResultSchema.nullable(),
    status: OfferStatusSchema,
    expiresAt: TimestampSchema,
})
    .meta({ id: 'Offer' });
export const FormalizationSchema = z
    .object({
    status: FormalizationStatusSchema,
    envelopeId: z.string().nullable(),
    lineId: CreditLineIdSchema.nullable(),
    completedAt: TimestampSchema.nullable(),
})
    .meta({ id: 'Formalization' });
// ── Expediente ────────────────────────────────────────────────────────────
export const CreditApplicationSchema = z
    .object({
    id: ApplicationIdSchema,
    customerId: CustomerIdSchema,
    productCode: z.string(),
    phase: ApplicationPhaseSchema,
    status: ApplicationStatusSchema,
    requestedAmount: MoneySchema.nullable(),
    purpose: z.string().nullable(),
    businessProfile: BusinessProfileSchema,
    householdProfile: HouseholdProfileSchema,
    completeness: z.string().meta({ description: 'Porcentaje 0–100 con 2 decimales.' }),
    checklist: z.array(ChecklistItemSchema),
    documents: z.array(EvidenceDocumentSchema),
    offer: OfferSchema.nullable(),
    decision: CreditDecisionSchema.nullable(),
    formalization: FormalizationSchema.nullable(),
    nextSteps: z.array(z.string()),
    agentSessionId: AgentSessionIdSchema.nullable(),
    lastActivityAt: TimestampSchema,
    createdAt: TimestampSchema,
})
    .meta({ id: 'CreditApplication' });
export const RecordConsentRequestSchema = z
    .object({
    type: ConsentTypeSchema,
    accepted: z.boolean(),
    textVersion: z.string(),
    otpChallengeId: z.string().optional(),
})
    .meta({ id: 'RecordConsentRequest' });
export const ApplicationActionSchema = z.enum(['SUBMIT', 'WITHDRAW', 'RESUME']);
export const ApplicationActionRequestSchema = z
    .object({ action: ApplicationActionSchema })
    .meta({ id: 'ApplicationActionRequest' });
export const AcceptOfferRequestSchema = z.object({ scaToken: z.string().min(16) });
export const AcceptOfferResponseSchema = z.object({
    phase: z.literal('FORMALIZATION'),
    envelopeId: z.string(),
});
export const SignFormalizationRequestSchema = z.object({ code: z.string().regex(/^\d{6}$/) });
export const SignFormalizationResponseSchema = z.object({
    phase: z.literal('ACTIVE'),
    lineId: CreditLineIdSchema,
});
export const PhaseChangeSchema = z.object({
    from: ApplicationPhaseSchema,
    to: ApplicationPhaseSchema,
    actorType: PhaseActorSchema,
    reason: z.string().nullable(),
    at: TimestampSchema,
});
export const ReturnToPhaseRequestSchema = z.object({
    phase: ApplicationPhaseSchema,
    reason: z.string().min(5),
});
export const AdminApplicationQuerySchema = PaginationQuerySchema.extend({
    status: ApplicationStatusSchema.optional(),
    phase: ApplicationPhaseSchema.optional(),
    assignedTo: z.string().optional(),
});
export const ApplicationPageSchema = paginatedSchema(CreditApplicationSchema);
export const RequiredDocumentStatusSchema = z.object({
    type: EvidenceTypeSchema,
    satisfied: z.boolean(),
    reasonCode: ReasonCodeSchema.nullable(),
});
//# sourceMappingURL=schemas.js.map